import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';

import { DisplayContactPerson } from '@pflegia/types/api/contact/display-contact-person';
import { ApplicationSource } from '@pflegia/types/common/enums';
import { ConnectionInfo } from '@pflegia/types/common/match/dialog';
import Bullet from '@pflegia/ui/shared/bullet/Bullet';
import { getButtonClassName } from '@pflegia/ui/shared/button';
import ButtonLink from '@pflegia/ui/shared/button-link/ButtonLink';
import { getPublicImageUrl } from '@pflegia/util/functions/s3/get-public-image-url';

import NoContactPerson from '@public/assets/no-contact-person.png';

import { SLIMMED_DOWN_FUNNEL } from '@shared/application/constants';
import OffersDialogueButton from '@shared/components/dialogue/OffersDialogueButton';
import { useRouterHelper } from '@shared/hooks/use-router-helper';

export interface ContactPersonTransparentProps {
  contactPerson?: DisplayContactPerson;
  jobPositionId?: string;
  jobPositionTitle?: string;
  jobPositionSlug: string;
  employerName?: string;
  openDialog: (connectionInfo: ConnectionInfo) => void;
}

export const ContactPersonTransparent: React.FC<ContactPersonTransparentProps> = ({
  contactPerson,
  jobPositionId,
  jobPositionTitle,
  jobPositionSlug,
  employerName,
  openDialog,
}) => {
  const { t } = useTranslation('jobDetails', { keyPrefix: 'contactPerson' });
  const { isExternalJob } = useRouterHelper();

  const label = t('apply');
  const bullets = t('bullets', { returnObjects: true }) as { title: string; description: string }[];

  return (
    <div className='text-default relative mx-auto w-full rounded-2xl bg-white px-8 py-4'>
      <h2 className='mb-3 text-lg font-bold lg:text-2xl'>{t('title')}</h2>

      <div className='flex flex-col items-center py-3 md:flex-row'>
        <div className='relative h-44 w-44 shrink-0 overflow-hidden rounded-2xl md:h-20 md:w-20 md:rounded-full'>
          <Image
            src={getPublicImageUrl(contactPerson?.pictureKey) ?? NoContactPerson}
            fill
            alt={t('contactPerson.title')}
            className='object-cover'
            unoptimized={Boolean(contactPerson?.pictureKey)}
          />
        </div>

        <div className='my-4 flex-1 flex-col text-center md:my-0 md:ml-4 md:text-left'>
          <h3 className='text-lg font-semibold'>
            {contactPerson?.name || t('contactPersonFirstName')} {contactPerson?.surname || t('contactPersonSurName')}
          </h3>
          <p className='mb-3 text-base font-normal'>{contactPerson?.jobTitle}</p>

          {isExternalJob ? (
            <ButtonLink
              href={{
                pathname: SLIMMED_DOWN_FUNNEL,
                query: { jobId: jobPositionId, external: isExternalJob, applicationSource: ApplicationSource.MultipostingRegistration },
              }}
              className={getButtonClassName('primary', 'w-full px-5! md:w-auto')}>
              {label}
            </ButtonLink>
          ) : (
            <OffersDialogueButton
              className='w-full px-5!'
              jobPositionId={jobPositionId || ''}
              label={label}
              jobTitle={jobPositionTitle || ''}
              employerName={employerName}
              openDialog={openDialog}
              colorScheme='primary'
              slug={jobPositionSlug}
            />
          )}
        </div>
      </div>

      <div className='mt-2 border-t border-gray-200 pt-4'>
        <span className='text-lg font-semibold'>{t('applicationProcessHeading')}</span>

        <div className='relative pt-3'>
          <div className='bg-primary xs:bottom-16 absolute top-8 bottom-24 left-3.75 w-0.5 sm:bottom-8 lg:bottom-10' />

          {bullets.map((bullet, index) => (
            <div key={bullet.title} className='relative mb-6 flex items-start last:mb-0'>
              <div className='relative z-10 mt-1'>
                <Bullet step={index + 1} className='bg-extended-jobs-background text-primary h-8 w-8 min-w-8' />
              </div>

              <div className='ml-5 flex-1 text-left text-base'>
                <p className='font-semibold'>{bullet.title}</p>
                <p>{bullet.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
